import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { firebaseApp } from '../../system/firebase';
import { TopBar } from '../../_shared/TopBar';
import Lottie from 'lottie-react';
import anim from '../../assets/anim/success.json';
import { FaMobileAlt, FaCreditCard, FaUniversity, FaCopy, FaCheck } from 'react-icons/fa';
import './donate.scss';

export const RouteDonate = () => {
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);

  // Form fields state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currency, setCurrency] = useState('KES');
  const [amount, setAmount] = useState('2500');
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');

  // Payment methods state
  const [paymentMethod, setPaymentMethod] = useState('mpesa');

  // Bank specific state
  const [bankRef, setBankRef] = useState('');
  const [copiedText, setCopiedText] = useState(false);

  // Transaction screen flow state
  const [screen, setScreen] = useState(0); // 0: Form, 1: Success
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  // Amount Presets
  const presetsKES = ['1000', '2500', '5000', '10000'];
  const presetsUSD = ['10', '25', '50', '100'];

  const getActivePresets = () => {
    return currency === 'KES' ? presetsKES : presetsUSD;
  };

  const handlePresetSelect = (val) => {
    setAmount(val);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e) => {
    const val = e.target.value;
    setCustomAmount(val);
    setAmount('');
  };

  const handleCurrencyChange = (newCurr) => {
    setCurrency(newCurr);
    if (newCurr === 'KES') {
      setAmount('2500');
    } else {
      setAmount('25');
      // Paystack doesn't support mobile money in USD, fallback to card
      if (paymentMethod === 'mpesa') {
        setPaymentMethod('card');
      }
    }
    setCustomAmount('');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText('01201244471500');
    setCopiedText(true);
    setTimeout(() => setCopiedText(false), 2000);
  };

  const handleDonate = async (e) => {
    e.preventDefault();

    // Validation
    if (!name.trim()) {
      alert('Please enter your full name.');
      return;
    }
    if (!email.trim()) {
      alert('Please enter your email address.');
      return;
    }

    const finalAmount = customAmount ? customAmount : amount;
    if (!finalAmount || parseFloat(finalAmount) <= 0) {
      alert('Please enter or select a valid donation amount.');
      return;
    }

    if (paymentMethod === 'mpesa' || paymentMethod === 'card') {
      if (!window.PaystackPop) {
        alert('Payment gateway is loading. Please wait a moment and try again.');
        return;
      }

      setLoading(true);
      setLoadingMessage('Initializing secure payment window...');

      try {
        const paystackKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || process.env.REACT_APP_PAYSTACK_TEST_PUBLIC_KEY;
        if (!paystackKey) {
          console.error('Neither REACT_APP_PAYSTACK_PUBLIC_KEY nor REACT_APP_PAYSTACK_TEST_PUBLIC_KEY is defined in environment variables.');
          alert('Configuration error: Public Key is missing.');
          setLoading(false);
          return;
        }

        const reference = `ABSK-DONATE-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

        const handler = window.PaystackPop.setup({
          key: paystackKey,
          email: email.toLowerCase().trim(),
          amount: Math.round(parseFloat(finalAmount) * 100), // in subunits (cents)
          currency: currency.toUpperCase(),
          ref: reference,
          channels: paymentMethod === 'mpesa' ? ['mobile_money'] : ['card'],
          metadata: {
            custom_fields: [
              {
                display_name: 'Donor Name',
                variable_name: 'donor_name',
                value: name.trim(),
              },
              {
                display_name: 'Message',
                variable_name: 'message',
                value: message.trim(),
              }
            ],
          },
          callback: function (response) {
            setLoadingMessage('Verifying transaction securely... Please do not close this window.');
            const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://us-central1-abskweb.cloudfunctions.net/profile';
            fetch(`${apiBaseUrl}/api/verify-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ reference: response.reference }),
            })
            .then(function (res) {
              return res.json().then(function (resData) {
                if (res.ok && resData.status === 'success') {
                  setLoading(false);
                  setScreen(1);
                } else {
                  setLoading(false);
                  alert(resData.message || 'Payment verification failed. Please contact support.');
                }
              });
            })
            .catch(function (err) {
              console.error('Verification error:', err);
              setLoading(false);
              alert(`Network error verifying payment. Please retain your reference: ${response.reference} and contact support.`);
            });
          },
          onClose: function () {
            setLoading(false);
          },
        });
        handler.openIframe();
      } catch (err) {
        console.error('Paystack initialization error:', err);
        setLoading(false);
        alert('Failed to initialize payment popup. Please try again.');
      }

    } else if (paymentMethod === 'bank') {
      if (!bankRef.trim()) {
        alert('Please enter the Bank Transfer Reference Number.');
        return;
      }

      setLoading(true);
      setLoadingMessage('Submitting transfer details for verification...');

      setTimeout(async () => {
        try {
          await addDoc(collection(db, 'ALL_DONATIONS'), {
            name: name.trim(),
            email: email.toLowerCase().trim(),
            currency,
            amount: parseFloat(finalAmount),
            paymentMethod: 'Bank Transfer',
            bankReference: bankRef.trim(),
            message: message.trim(),
            status: 'pending_verification',
            time: serverTimestamp(),
          });

          setLoading(false);
          setScreen(1);
        } catch (err) {
          console.error(err);
          setLoading(false);
          setScreen(1);
        }
      }, 2000);
    }
  };

  return (
    <div className="screen-donate">
      <TopBar />

      {/* Navigation */}
      <nav className="donate-nav">
        <div className="nav-container">
          <a href="/index" className="navbar-brand">
            <span className="image-text">ABSK</span> <sup>Africa</sup>
          </a>
          <ul className="nav-links">
            <li><a href="/index#home">Home</a></li>
            <li><a href="/webinars">Events</a></li>
            <li><a href="/index#about">About Us</a></li>
          </ul>
        </div>
      </nav>

      {/* Main Donation Container */}
      {screen === 0 ? (
        <div className="donate-card">
          <div className="donate-hero">
            <div className="hero-content">
              <h1>Support ABSK Africa</h1>
              <p>
                Your donation directly funds counselling programs, research, and certificates to empower African individuals and communities in the spirit of Ubuntu.
              </p>
            </div>
          </div>

          <form className="donate-body" onSubmit={handleDonate}>
            {/* Donor info */}
            <div className="row-grid">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  placeholder="Enter your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Currency Choice */}
            <div className="form-group">
              <label>Select Currency</label>
              <div className="amount-presets" style={{ gridTemplateColumns: 'repeat(2, 1fr)', maxWidth: '300px' }}>
                <button
                  type="button"
                  className={`preset-btn ${currency === 'KES' ? 'active' : ''}`}
                  onClick={() => handleCurrencyChange('KES')}
                >
                  KES (KSh)
                </button>
                <button
                  type="button"
                  className={`preset-btn ${currency === 'USD' ? 'active' : ''}`}
                  onClick={() => handleCurrencyChange('USD')}
                >
                  USD ($)
                </button>
              </div>
            </div>

            {/* Donation Amount presets */}
            <div className="form-group">
              <label>Select Donation Amount</label>
              <div className="amount-presets">
                {getActivePresets().map((val) => (
                  <button
                    key={val}
                    type="button"
                    className={`preset-btn ${amount === val ? 'active' : ''}`}
                    onClick={() => handlePresetSelect(val)}
                  >
                    {currency === 'KES' ? 'KES ' : '$'}{parseFloat(val).toLocaleString()}
                  </button>
                ))}
              </div>
              <div className="custom-amount-wrapper">
                <span className="currency-symbol">{currency}</span>
                <input
                  type="number"
                  placeholder="Or enter custom amount"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  min="1"
                />
              </div>
            </div>

            {/* Optional message */}
            <div className="form-group">
              <label>Leave a Message (Optional)</label>
              <textarea
                placeholder="Share your thoughts or specify if you wish to sponsor a program"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            {/* Payment Method Selector */}
            <div className="payment-methods-header">
              <h3>Payment Method</h3>
              <div className="payment-tabs">
                {currency === 'KES' && (
                  <button
                    type="button"
                    className={`tab-btn ${paymentMethod === 'mpesa' ? 'active' : ''}`}
                    onClick={() => setPaymentMethod('mpesa')}
                  >
                    <FaMobileAlt /> M-Pesa
                  </button>
                )}
                <button
                  type="button"
                  className={`tab-btn ${paymentMethod === 'card' ? 'active' : ''}`}
                  onClick={() => setPaymentMethod('card')}
                >
                  <FaCreditCard /> Credit/Debit Card
                </button>
                <button
                  type="button"
                  className={`tab-btn ${paymentMethod === 'bank' ? 'active' : ''}`}
                  onClick={() => setPaymentMethod('bank')}
                >
                  <FaUniversity /> Bank Transfer
                </button>
              </div>
            </div>

            {/* Payment content */}
            <div className="payment-tab-content">
              {paymentMethod === 'mpesa' && (
                <div className="mpesa-simulation-form">
                  <p className="info" style={{ marginBottom: 0 }}>
                    We process M-Pesa payments securely via Paystack. When you click the <strong>Donate</strong> button below, a secure payment window will open prompting you to enter your phone number and authorize the transaction with your Safaricom M-Pesa PIN.
                  </p>
                </div>
              )}

              {paymentMethod === 'card' && (
                <div className="card-payment-form">
                  <div className="card-icons">
                    <FaCreditCard className="active-card" />
                  </div>
                  <p className="info" style={{ color: '#64748b', fontSize: '13px', lineHeight: '1.4', margin: 0 }}>
                    We accept all major debit and credit cards (Visa, Mastercard, American Express). Your card information is encrypted and processed securely by Paystack. When you click the <strong>Donate</strong> button below, a secure payment form will open where you can safely enter your card details.
                  </p>
                </div>
              )}

              {paymentMethod === 'bank' && (
                <div className="bank-details">
                  <h4>Transfer directly to ABSK Africa NBK Account</h4>
                  <div className="detail-row">
                    <span className="label">Bank Name</span>
                    <span className="value">National Bank Of Kenya</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Account Name</span>
                    <span className="value">ABSK Africa</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Account Number</span>
                    <span className="value">
                      01201244471500
                      <button type="button" className="copy-btn" onClick={copyToClipboard}>
                        {copiedText ? <FaCheck style={{ color: '#4caf50' }} /> : <FaCopy />}
                      </button>
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Branch</span>
                    <span className="value">Harambee Avenue, Nairobi</span>
                  </div>

                  <div className="form-group" style={{ marginTop: '20px', marginBottom: 0 }}>
                    <label>Bank Transfer Reference Number</label>
                    <input
                      type="text"
                      placeholder="Enter transaction ID / slip reference"
                      value={bankRef}
                      onChange={(e) => setBankRef(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            <button type="submit" className="btn-submit-donation">
              Donate {currency === 'KES' ? 'KES ' : '$'}{customAmount ? parseFloat(customAmount).toLocaleString() : parseFloat(amount).toLocaleString()}
            </button>
          </form>
        </div>
      ) : (
        /* Success Screen */
        <div className="donate-card success-view">
          <div className="success-lottie">
            <Lottie animationData={anim} loop={false} />
          </div>
          <h2>Thank You!</h2>
          <p>
            Your donation of <strong>{currency} {parseFloat(customAmount || amount).toLocaleString()}</strong> was recorded successfully. We appreciate your support for ABSK Africa's programs.
          </p>
          <a href="/index" className="btn-back-home">
            Back to Homepage
          </a>
        </div>
      )}

      {/* Simulator Overlay */}
      {loading && (
        <div className="sim-loader-overlay">
          <div className="loader-card">
            <div className="lds-spinner spinner-orange" style={{ margin: '0 auto' }}>
              <div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div>
            </div>
            <p>{loadingMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteDonate;
